// src/AccountAction.js — where the links in our account emails land (app.frisoapp.com/account?mode=…&oobCode=…), in
// place of Firebase's plain /__/auth/action page (approved mockup: "FriSo Account Pages" canvas, 2026-10-18).
// index.js renders this instead of App on /account, so nothing else loads or subscribes. Firebase still makes and
// checks the one-time codes; this is only the page. Modes we don't send (e.g. recoverEmail) go to Firebase's page.
import "./App.css";
import { useEffect, useRef, useState } from "react";
import { applyActionCode, checkActionCode, confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "./firebase";
import { Shell, ErrorLine, authMessage } from "./AuthScreen";

const APP_URL = "/";
const params = new URLSearchParams(window.location.search);
const mode = params.get("mode");
const code = params.get("oobCode") || "";

const Icon = ({ tone, children }) => (
  <div className={`auth-mailicon auth-mailicon--${tone}`} aria-hidden="true">
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
  </div>
);
const LockIcon = () => <Icon tone="rose"><rect x="4.5" y="10.5" width="15" height="10" rx="2.5" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></Icon>;
const CheckIcon = () => <Icon tone="mint"><path d="M5 12.5l4.5 4.5L19 7.5" strokeWidth="2.4" /></Icon>;
const MailCheckIcon = () => <Icon tone="mint"><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.5 6.5l8.5 6.5 8.5-6.5" /><path d="M14.5 17l2 2 4-4.5" strokeWidth="2.4" /></Icon>;
const ClockIcon = () => <Icon tone="butter"><circle cx="12" cy="13" r="8" /><path d="M12 9v4.5l2.5 1.5" /><path d="M9.5 2.5h5" /></Icon>;

export default function AccountAction() {
  // "checking" | "reset" | "resetDone" | "confirmed" | "expired"
  const [stage, setStage] = useState("checking");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const started = useRef(false); // a code works once; don't check it twice (StrictMode runs effects twice in dev)

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (mode === "resetPassword") {
      verifyPasswordResetCode(auth, code).then((em) => { setEmail(em); setStage("reset"); }).catch(() => setStage("expired"));
    } else if (mode === "verifyEmail") {
      checkActionCode(auth, code)
        .then((res) => { setEmail(res.data.email || ""); return applyActionCode(auth, code); })
        .then(() => setStage("confirmed"))
        .catch(() => setStage("expired"));
    } else {
      window.location.replace(`/__/auth/action${window.location.search}`);
    }
  }, []);

  const savePassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Use at least 8 characters."); return; }
    setBusy(true); setError("");
    try {
      await confirmPasswordReset(auth, code, password);
      setStage("resetDone");
    } catch (err) {
      if (err.code === "auth/expired-action-code" || err.code === "auth/invalid-action-code") setStage("expired");
      else setError(authMessage(err));
    }
    setBusy(false);
  };

  const sendNewLink = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setInfo("");
    try {
      await httpsCallable(functions, "sendPasswordReset")({ email: email.trim().toLowerCase() });
      setInfo("If there's a FriSo account for that email, a new link is on its way.");
    } catch (err) {
      setError(err.code === "functions/invalid-argument" ? err.message : "Couldn't send the email. Please try again.");
    }
    setBusy(false);
  };

  if (stage === "checking") return <Shell hero={false}><div className="auth-card auth-card--center"><p className="auth-info" role="status">Checking your link…</p></div></Shell>;

  return (
    <Shell hero={false}>
      {stage === "reset" && (
        <form className="auth-card auth-card--center" onSubmit={savePassword} noValidate>
          <LockIcon />
          <div className="auth-heading">
            <h2>Choose a new password</h2>
            <p>For <strong>{email}</strong></p>
          </div>
          <div className="auth-field">
            <div className="auth-field__top">
              <label htmlFor="acct-pw">New password</label>
              <button type="button" className="auth-linkbtn" onClick={() => setShow(!show)}>{show ? "Hide" : "Show"}</button>
            </div>
            <input id="acct-pw" className={`form-input${error ? " form-input--error" : ""}`} type={show ? "text" : "password"} autoComplete="new-password"
              value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} aria-describedby="acct-pw-hint" />
            <span id="acct-pw-hint" className="auth-hint">At least 8 characters.</span>
          </div>
          {error && <ErrorLine>{error}</ErrorLine>}
          <button type="submit" className="btn btn--primary btn--full auth-btn" disabled={busy}>{busy ? "Saving…" : "Save password"}</button>
          <p className="auth-note">Remembered it? <a className="auth-linkbtn" href={APP_URL}>Log in</a></p>
        </form>
      )}

      {stage === "resetDone" && (
        <div className="auth-card auth-card--center">
          <CheckIcon />
          <div className="auth-heading">
            <h2>Password changed</h2>
            <p>You can log in with your new password now.</p>
          </div>
          <a className="btn btn--primary btn--full auth-btn" href={APP_URL}>Log in to FriSo</a>
        </div>
      )}

      {stage === "confirmed" && (
        <div className="auth-card auth-card--center">
          <MailCheckIcon />
          <div className="auth-heading">
            <h2>Your email is confirmed</h2>
            <p>Thanks! {email ? <><strong>{email}</strong> is all set.</> : "You're all set."} Open FriSo to start planning with your people.</p>
          </div>
          <a className="btn btn--primary btn--full auth-btn" href={APP_URL}>Open FriSo</a>
          <p className="auth-note">Signed up on another device? You can go back to it now.</p>
        </div>
      )}

      {stage === "expired" && mode === "resetPassword" && (
        <form className="auth-card auth-card--center" onSubmit={sendNewLink} noValidate>
          <ClockIcon />
          <div className="auth-heading">
            <h2>This link has expired</h2>
            <p>Links in FriSo emails only work once, for a short time. Enter your email and we'll send you a new one.</p>
          </div>
          <div className="auth-field">
            <label htmlFor="acct-email">Email</label>
            <input id="acct-email" className="form-input" type="email" autoComplete="email" placeholder="you@example.com"
              value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} />
          </div>
          {error && <ErrorLine>{error}</ErrorLine>}
          {info && <p className="auth-info" role="status">{info}</p>}
          <button type="submit" className="btn btn--primary btn--full auth-btn" disabled={busy || !email.trim()}>{busy ? "Sending…" : "Send a new link"}</button>
          <p className="auth-note"><a className="auth-linkbtn" href={APP_URL}>Back to log in</a></p>
        </form>
      )}

      {stage === "expired" && mode !== "resetPassword" && (
        <div className="auth-card auth-card--center">
          <ClockIcon />
          <div className="auth-heading">
            <h2>This link has expired</h2>
            <p>Links in FriSo emails only work once, for a short time. Log in and tap “Send it again” to get a new one. If you already confirmed, you're all set.</p>
          </div>
          <a className="btn btn--primary btn--full auth-btn" href={APP_URL}>Log in to FriSo</a>
        </div>
      )}
    </Shell>
  );
}
